/**
 * Centralized date formatting and parsing. Use for API params (YYYY-MM-DD, YYYY-MM) and display.
 * **API payloads and calendar keys must use English numerals and stable YMD** — use `locale('en')` before
 * formatting so global `moment.locale('ar')` never leaks Arabic-Indic digits into keys/parsers.
 */
import moment from 'moment';
import type { Moment } from 'moment';
import { ensureAsciiDateString } from './apiParams';

const DISPLAY_ORDER_DATE_FORMATS = ['Do MMMM, YYYY', 'YYYY-MM-DD', 'MMM DD, YYYY', 'DD MMMM, YYYY'] as const;
const NORMALIZE_CALENDAR_FORMATS = [
  'YYYY-MM-DD',
  'DD-MM-YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
  'DD MMM YYYY',
  'DD MMMM, YYYY',
] as const;

/**
 * Format date for display in order history/details (e.g. "MMM D, YYYY").
 */
export function formatOrderDate(date_str: string | undefined): string {
  if (!date_str) {return '—';}
  const ascii = ensureAsciiDateString(date_str) || date_str;
  const parsed = moment(ascii, [...DISPLAY_ORDER_DATE_FORMATS], true);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : date_str;
}

/**
 * Normalize various date strings to YYYY-MM-DD for API/calendar keys.
 */
export function normalizeCalendarDate(date_str: string | undefined): string {
  if (!date_str || typeof date_str !== 'string') {
    return '';
  }
  const ascii = ensureAsciiDateString(date_str) || date_str;
  const m = moment(ascii, [...NORMALIZE_CALENDAR_FORMATS], true);
  return m.isValid() ? m.clone().locale('en').format('YYYY-MM-DD') : ascii;
}

/**
 * Format a Moment as `YYYY-MM-DD` for API payloads (always English locale / ASCII digits).
 * Belt-and-suspenders: also strip any Arabic-Indic digits if a locale postformat leaks through.
 */
export function formatMomentForApiYmd(m: Moment): string {
  return ensureAsciiDateString(m.clone().locale('en').format('YYYY-MM-DD'));
}

/**
 * Current date as YYYY-MM-DD (ASCII). Prefer this over `moment().format('YYYY-MM-DD')`
 * when the global Moment locale may be Arabic.
 */
export function formatTodayForApi(): string {
  return ensureAsciiDateString(moment().locale('en').format('YYYY-MM-DD'));
}

/**
 * Current month as YYYY-MM (for API params).
 */
export function formatCurrentMonthForApi(): string {
  return ensureAsciiDateString(moment().locale('en').format('YYYY-MM'));
}

/**
 * Format a Date or date string as YYYY-MM-DD (ASCII).
 * String inputs are digit-normalized first so Arabic-Indic YMD still parses.
 */
export function formatDateForApi(date: Date | string): string {
  if (typeof date === 'string') {
    const ascii = ensureAsciiDateString(date) || date;
    const strict = moment(ascii, 'YYYY-MM-DD', true);
    if (strict.isValid()) {
      return ensureAsciiDateString(strict.locale('en').format('YYYY-MM-DD'));
    }
    return ensureAsciiDateString(moment(ascii).locale('en').format('YYYY-MM-DD'));
  }
  return ensureAsciiDateString(moment(date).locale('en').format('YYYY-MM-DD'));
}

/**
 * Format a Date or date string as YYYY-MM.
 */
export function formatMonthForApi(date: Date | string): string {
  if (typeof date === 'string') {
    const ascii = ensureAsciiDateString(date) || date;
    return ensureAsciiDateString(moment(ascii).locale('en').format('YYYY-MM'));
  }
  return ensureAsciiDateString(moment(date).locale('en').format('YYYY-MM'));
}

/**
 * Parse a calendar/API YMD key into a Moment (strict `YYYY-MM-DD` after ASCII digit normalize).
 * Use before `.format(...)` for display labels when the key may have come from a localized formatter.
 */
export function parseYmdMoment(ymd: string | undefined | null): Moment {
  const ascii = ensureAsciiDateString(ymd) || '';
  return moment(ascii, 'YYYY-MM-DD', true);
}
